import React, { FC, Fragment, ReactElement, useContext, useMemo } from 'react'
import { BRANDING_ENABLED_FIELD_NAME } from '../FieldName'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { BrandingLanguage } from '../../../../i18n'
import FormRow from '../../Common/Components/FormRow'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { BrandingArea, BrandingConfig, BrandingConfigs, BrandingTab, CMEBranding, CMEFormHookData } from '../Types'
import { useConfig } from '../../Common/Hooks/useConfig'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { BrandingFormEntries } from './BrandingFormEntries'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { IconName } from '../../Common/Components/Icon'
import { BrandingLoader } from './BrandingLoader'
import { BrandingType, deleteBrandingConfigByType, deleteBrandingIDFromUserSession } from '../../Common/API/BrandingAPI'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { currentUserState$ } from '../../../NonReact/common/RxJS/UserInfoState'

const BRANDING_CONFIG_KEY = 'branding-config' as const
const PRIMARY_KEY = 'brandingConfig' as const

type Props = {
    viewOnly: boolean
    selectedCME?: string
    isResetAvailable?: boolean
    isPreviewAvailable?: boolean
    userBrandingID: Nullable<string>
    areDirtyBrandingFields?: boolean
    replaceFormContentWithPreviousBranding: () => Promise<unknown>
    replaceFormContentWithCurrentBranding: () => Promise<unknown>
    replaceFormContentWithPreviewBranding: () => Promise<void>
    applyPreviewBranding: () => Promise<void>
    existingBranding?: CMEBranding
    activeBrandingTabState: ReactState<string>
    currentPreviewBrandingID?: Nullable<string>
    isPreviewDisplayedState: ReactState<boolean>
    isResetDisplayedState: ReactState<boolean>
    isLoadingState: ReactState<boolean>
}

export const Branding: FC<Props> = ({
    viewOnly,
    selectedCME,
    isResetAvailable,
    isPreviewAvailable,
    userBrandingID,
    areDirtyBrandingFields,
    replaceFormContentWithPreviousBranding,
    replaceFormContentWithCurrentBranding,
    replaceFormContentWithPreviewBranding,
    applyPreviewBranding,
    existingBranding,
    currentPreviewBrandingID,
    activeBrandingTabState: [activeTab, setActiveTab],
    isPreviewDisplayedState: [isPreviewDisplayed, setIsPreviewDisplayed],
    isResetDisplayedState: [isResetDisplayed, setIsResetDisplayed],
    isLoadingState: [isLoading, setIsLoading]
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(BrandingLanguage.NAME)

    const { errors, reset, watch }: UseFormMethods<CMEFormHookData> = useFormContext()

    const brandingEnabled: Definable<boolean> = watch(BRANDING_ENABLED_FIELD_NAME, false)

    const brandingConfigs: Nullable<BrandingConfigs> = useConfig<BrandingConfigs>(BRANDING_CONFIG_KEY, PRIMARY_KEY)

    const tabs: Nullable<BrandingTab[]> = useMemo(
        (): Nullable<BrandingTab[]> =>
            brandingConfigs
                ?.map(
                    (
                        { id, title, entries, rootOnly }: BrandingArea<BrandingConfig>,
                        index: number
                    ): Definable<BrandingTab> =>
                        !rootOnly || (rootOnly && selectedCME === DEFAULT_CME_ID)
                            ? {
                                  id: id ?? EMPTY_STRING,
                                  label: language.get(title),
                                  isError: isVarNotEmpty(errors?.branding?.[id ?? EMPTY_STRING]),
                                  component: (isTabActive: boolean): ReactElement => (
                                      <BrandingFormEntries
                                          key={`branding-form-entries-${id}-${title}-${index}`}
                                          viewOnly={viewOnly}
                                          selectedCME={selectedCME}
                                          brandingConfig={entries}
                                          parentID={id ?? EMPTY_STRING}
                                          existingBranding={existingBranding}
                                          isTabActive={isTabActive}
                                      />
                                  )
                              }
                            : undefined
                )
                .filter(isVarNotEmpty),
        [brandingConfigs, language, selectedCME, viewOnly, JSON.stringify(errors?.branding)]
    )

    // If the reset button is displayed, the restore button should be displayed as well.
    const shouldDisplayResetButton: boolean = useMemo(
        (): boolean =>
            !!(
                isResetAvailable &&
                isStringNotEmpty(selectedCME) &&
                IsStringEmpty(userBrandingID) &&
                !isResetDisplayed &&
                !isPreviewDisplayed
            ),
        [isResetAvailable, selectedCME, userBrandingID, isResetDisplayed, isPreviewDisplayed]
    )

    // If the reset button is displayed, the restore button should be displayed as well.
    const shouldDisplayRestoreButton: boolean = useMemo(
        (): boolean => (isResetDisplayed || isPreviewDisplayed) && isStringNotEmpty(selectedCME),
        [isResetDisplayed, isPreviewDisplayed, selectedCME]
    )

    // If the preview button is displayed, the restore button should be displayed as well.
    const shouldDisplayExistingPreviewButton: boolean = useMemo(
        (): boolean =>
            !!(
                isPreviewAvailable &&
                isStringNotEmpty(selectedCME) &&
                (IsStringEmpty(userBrandingID) || userBrandingID !== currentPreviewBrandingID) &&
                isStringNotEmpty(currentPreviewBrandingID) &&
                !isPreviewDisplayed &&
                !isResetDisplayed &&
                !areDirtyBrandingFields
            ),
        [
            isPreviewAvailable,
            selectedCME,
            userBrandingID,
            currentPreviewBrandingID,
            isPreviewDisplayed,
            isResetDisplayed,
            areDirtyBrandingFields
        ]
    )

    // If the preview button is displayed, the restore button should be displayed as well.
    const shouldShowNewPreviewBrandingButton: boolean = useMemo(
        (): boolean => !!(isStringNotEmpty(selectedCME) && areDirtyBrandingFields && !isResetDisplayed),
        [areDirtyBrandingFields, selectedCME, isResetDisplayed]
    )

    const shouldShowPreviewDeleteButton: boolean = useMemo(
        (): boolean =>
            !!(
                isStringNotEmpty(selectedCME) &&
                isPreviewDisplayed &&
                !isResetDisplayed &&
                isStringNotEmpty(currentPreviewBrandingID)
            ),
        [selectedCME, isPreviewDisplayed, isResetDisplayed]
    )

    const isEditMode: boolean = useMemo((): boolean => isStringNotEmpty(selectedCME), [selectedCME])

    return (
        <div className='cme-branding-tab tab-area'>
            {isLoading ? (
                <BrandingLoader />
            ) : (
                <>
                    <FormRow className='cmebt-branding-controls-container'>
                        <RouterCheckbox
                            className='cmebt-branding-enabled'
                            labelLeft={language.get(BrandingLanguage.Branding_Labels_BrandingEnabled)}
                            name={BRANDING_ENABLED_FIELD_NAME}
                            disabled={viewOnly || selectedCME === DEFAULT_CME_ID}
                            onChange={(checked: boolean): void => {
                                if (!checked) {
                                    reset({
                                        ...watch(),
                                        branding: {
                                            brandingEnabled: false
                                        }
                                    })
                                }
                            }}
                        />

                        {!viewOnly && isEditMode ? (
                            <div className='cme-branding-controls'>
                                {areDirtyBrandingFields ? (
                                    <IconButton
                                        iconName={IconName.CLEAR_CHANGES}
                                        type={IconButtonType.BUTTON}
                                        title={language.get(BrandingLanguage.Branding_UndoChanges)}
                                        onClick={async (): Promise<void> => {
                                            setIsLoading(true)

                                            if (isPreviewDisplayed) {
                                                await replaceFormContentWithPreviewBranding()
                                            } else if (isResetDisplayed) {
                                                await replaceFormContentWithPreviousBranding()
                                            } else {
                                                await replaceFormContentWithCurrentBranding()
                                            }

                                            setIsLoading(false)
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                                {shouldShowPreviewDeleteButton ? (
                                    <IconButton
                                        iconName={IconName.DELETE_PREVIEW}
                                        type={IconButtonType.BUTTON}
                                        title={language.get(BrandingLanguage.Branding_DeletePreview)}
                                        onClick={async (): Promise<void> => {
                                            replaceFormContentWithCurrentBranding().then((): Promise<void> => {
                                                setIsLoading(true)

                                                return deleteBrandingIDFromUserSession().then((): void => {
                                                    deleteBrandingConfigByType(
                                                        selectedCME ?? EMPTY_STRING,
                                                        BrandingType.PREVIEW
                                                    )
                                                        .then((): void => {
                                                            setIsPreviewDisplayed(false)
                                                            window.location.reload()
                                                        })
                                                        .finally((): void => setIsLoading(false))
                                                })
                                            })
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                                {shouldDisplayResetButton ? (
                                    <IconButton
                                        iconName={IconName.REPLAY}
                                        type={IconButtonType.BUTTON}
                                        title={language.get(BrandingLanguage.Branding_ResetButton)}
                                        onClick={(): void => {
                                            replaceFormContentWithPreviousBranding().then((): void =>
                                                setIsResetDisplayed(true)
                                            )
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                                {shouldDisplayRestoreButton ? (
                                    <IconButton
                                        customClassName={isResetDisplayed ? 'cmebt-branding-restore' : EMPTY_STRING}
                                        iconName={IconName.REPLAY}
                                        type={IconButtonType.BUTTON}
                                        title={language.get(BrandingLanguage.Branding_RestoreCurrentVersion)}
                                        onClick={(): void => {
                                            replaceFormContentWithCurrentBranding().then((): Promise<void> => {
                                                setIsLoading(true)

                                                return deleteBrandingIDFromUserSession().then((): void => {
                                                    setIsResetDisplayed(false)
                                                    setIsPreviewDisplayed(false)
                                                    currentUserState$?.resolver?.(true)
                                                    setIsLoading(false)
                                                })
                                            })
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                                {shouldDisplayExistingPreviewButton || shouldShowNewPreviewBrandingButton ? (
                                    <IconButton
                                        iconName={IconName.PREVIEW}
                                        type={IconButtonType.BUTTON}
                                        title={
                                            shouldDisplayExistingPreviewButton
                                                ? language.get(BrandingLanguage.Branding_PreviewButton)
                                                : language.get(BrandingLanguage.Branding_ApplyPreviewButton)
                                        }
                                        onClick={(): void => {
                                            if (shouldDisplayExistingPreviewButton) {
                                                replaceFormContentWithPreviewBranding().then((): void =>
                                                    setIsPreviewDisplayed(true)
                                                )
                                            }

                                            if (shouldShowNewPreviewBrandingButton) {
                                                applyPreviewBranding().then((): void => setIsPreviewDisplayed(true))
                                            }
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                            </div>
                        ) : (
                            <></>
                        )}
                    </FormRow>
                    {brandingEnabled ? (
                        <div className='branding-tab-container'>
                            {tabs?.map(
                                ({ id, isError, label }: BrandingTab): ReactElement => (
                                    <RoundedButton
                                        key={id}
                                        type='button'
                                        onClick={(): void => setActiveTab(id)}
                                        isOutlined={activeTab !== id}
                                        showErrorBadge={isError}
                                    >
                                        {label}
                                    </RoundedButton>
                                )
                            )}
                        </div>
                    ) : (
                        <></>
                    )}
                    {tabs?.map(
                        ({ id, component }: BrandingTab): ReactElement => (
                            <Fragment key={id}>{brandingEnabled ? component(activeTab === id) : <></>}</Fragment>
                        )
                    )}
                </>
            )}
        </div>
    )
}
